 
import React, { useRef, useState, useEffect } from 'react';
import * as dsteem from 'dsteem';
import axios from 'axios';
import { extractSessionData } from 'helpers/localStorage';
import { isLoggedWithKeychain } from 'services/dsteem-service';
import { useAuth } from 'helpers/authContext';

interface Props {
  inputName: string;
  placeholder?: string;
  defaultValue?: string;
  defaultPreview?: string;
  register: any;
  onUpload: (url: string) => void;
  setErrorMessage: (msg: string) => void;
  setValue: (name: string, value: any, options?: object) => void;
  watch: (name: string) => any;
}

const ImageUploader: React.FC<Props> = ({
  inputName,
  placeholder = "Post cover image link",
  defaultValue,
  defaultPreview = '/assets/img/thump.png',
  register,
  onUpload,
  setErrorMessage,
  setValue,
  watch,
}) => {
  const { user, autologin } = useAuth();
  const [previewUrl, setPreviewUrl] = useState(defaultValue || defaultPreview);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const keychainLogin = isLoggedWithKeychain(autologin);

  const coverValue: string = watch(inputName);

  useEffect(() => { 
    if (coverValue && coverValue !== previewUrl) {
      setPreviewUrl(coverValue);
    }
  }, [coverValue]);

  const uploadImage = async (file: File) => {
    let username = user;
    let wif = null;

    if (!keychainLogin) {
      const session = extractSessionData(autologin);
      username = session[0];
      wif = session[1];
    }

    const IMAGE_API = 'https://steemitimages.com';
    const maxSizeMB = 5;
    const maxSize = maxSizeMB * 1024 * 1024;

    if (file.size > maxSize) {
      setErrorMessage('Add an image up to 5 MB.');
      return;
    }

    if (!file || !username) {
      setErrorMessage('Failed getting your session, try logging in again.');
      return;
    }

    if (!keychainLogin && !wif) {
      setErrorMessage('To upload an image you must log in with your username and posting key.');
      return;
    }

    setPreviewUrl('/assets/img/loading.png');

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageBase64 = event.target?.result?.toString().split(',')[1] || '';
        const photoBuf = new Uint8Array(Buffer.from(imageBase64, 'base64'));
        const prefix = new Uint8Array(Buffer.from('ImageSigningChallenge'));
        const data = Buffer.concat([prefix, photoBuf]);

        let signature: string;

        if (!keychainLogin) {
          const hash = dsteem.cryptoUtils.sha256(data);
          const privateKey = dsteem.PrivateKey.fromString(wif);
          signature = privateKey.sign(hash).toString();
        } else {
          const response = await new Promise<any>((resolve) => {
            window.steem_keychain.requestSignBuffer(
              username,
              JSON.stringify(data),
              'Posting',
              resolve
            );
          });

          signature = response.result;
        }

        const formData = new FormData();
        formData.append('encodedImage', imageBase64);
        formData.append('image', file);
        formData.append('signature', signature);
        formData.append('username', username);
        let response = null;
        try {
          response = await axios.post(`${IMAGE_API}/${username}/${signature}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (error) {
          setPreviewUrl(defaultPreview);
          console.log(error.message); 
          setErrorMessage(error.message || 'An error occurred.');
        }
        
        if (!response) {
          setPreviewUrl(defaultPreview);
          setErrorMessage('An error occurred.');
        }else {
          const url = response.data.url;
          setPreviewUrl(url);
          onUpload(url);
        }
        
      }; 
      reader.readAsDataURL(file);
    } catch (err: any) {
      setPreviewUrl(defaultPreview);
      setErrorMessage(err.message || 'An error occurred.');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(file);
    }
  };

  return (
    <div className="cover-box fancy-input" style={{ position: 'relative' }}>
      <input
        type="text"
        name={inputName}
        id={inputName}
        className="form-control no-border"
        placeholder={placeholder}
        value={defaultValue || coverValue || ''}
        onChange={(e) =>
          setValue(inputName, e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        {...register}
      />

      <input
        type="file"
        id="imageInput"
        accept="image/*"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: 'pointer',
          zIndex: 2,
        }}
      />

      <div id="preview-img-box">
        <img src={previewUrl} className="preview-img" alt="Preview" />
      </div>

      <label htmlFor="imageInput" className="btn btn-file select-file-button no-hover" style={{ position: 'relative', zIndex: 1 }}>
        Select File
      </label>
    </div>
  );
};

export default ImageUploader;
